import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { SenderProfile } from '../types';

// Loading Skeleton Card
const SkeletonCard: React.FC = () => (
  <div style={{ 
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e5e7eb'
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
      <div style={{ height: '20px', background: '#e5e7eb', borderRadius: '4px', width: '50%' }} />
      <div style={{ height: '24px', background: '#e5e7eb', borderRadius: '9999px', width: '60px' }} />
    </div>
    <div style={{ height: '14px', background: '#e5e7eb', borderRadius: '4px', width: '80%', marginBottom: '8px' }} />
    <div style={{ height: '14px', background: '#e5e7eb', borderRadius: '4px', width: '60%', marginBottom: '8px' }} />
    <div style={{ height: '14px', background: '#e5e7eb', borderRadius: '4px', width: '40%', marginBottom: '16px' }} />
    <div style={{ display: 'flex', gap: '8px' }}>
      <div style={{ height: '36px', background: '#e5e7eb', borderRadius: '6px', width: '120px' }} />
      <div style={{ height: '36px', background: '#e5e7eb', borderRadius: '6px', width: '80px' }} />
    </div>
  </div>
);

const SenderProfiles: React.FC = () => {
  const [profiles, setProfiles] = useState<SenderProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingProfile, setEditingProfile] = useState<SenderProfile | null>(null);
  const [testingProfile, setTestingProfile] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    from_email: '',
    from_name: '',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls' as 'tls' | 'ssl' | 'none',
    daily_limit: 1000,
    imap_host: 'imap.gmail.com',
    imap_port: 993,
    imap_username: '',
    imap_password: '',
    imap_encryption: 'ssl' as 'ssl' | 'tls' | 'none',
    imap_enabled: false,
  });

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/sender-profiles');
      setProfiles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch profiles', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        smtp_password: formData.smtp_password || undefined,
        imap_password: formData.imap_password || undefined,
      };

      if (editingProfile) {
        await api.put(`/sender-profiles/${editingProfile.id}`, payload);
      } else {
        await api.post('/sender-profiles', payload);
      }
      setShowModal(false);
      setEditingProfile(null);
      setFormData({
        name: '',
        from_email: '',
        from_name: '',
        smtp_host: 'smtp.gmail.com',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_encryption: 'tls',
        daily_limit: 1000,
        imap_host: 'imap.gmail.com',
        imap_port: 993,
        imap_username: '',
        imap_password: '',
        imap_encryption: 'ssl',
        imap_enabled: false,
      });
      fetchProfiles();
    } catch (error) {
      alert(editingProfile ? 'Failed to update sender profile' : 'Failed to save sender profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (profile: SenderProfile) => {
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      from_email: profile.from_email,
      from_name: profile.from_name,
      smtp_host: profile.smtp_host || 'smtp.gmail.com',
      smtp_port: profile.smtp_port || 587,
      smtp_username: profile.smtp_username || '',
      smtp_password: '',
      smtp_encryption: profile.smtp_encryption || 'tls',
      daily_limit: profile.daily_limit,
      imap_host: profile.imap_host || 'imap.gmail.com',
      imap_port: profile.imap_port || 993,
      imap_username: profile.imap_username || '',
      imap_password: '',
      imap_encryption: profile.imap_encryption || 'ssl',
      imap_enabled: profile.imap_enabled || false,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this sender profile?')) return;
    try {
      await api.delete(`/sender-profiles/${id}`);
      fetchProfiles();
    } catch (error) {
      alert('Failed to delete profile');
    }
  };

  const handleTest = async (id: number) => {
    setTestingProfile(id);
    try {
      await api.post(`/sender-profiles/${id}/test`, {});
      alert('Test email sent successfully!');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Test failed');
    } finally {
      setTestingProfile(null);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827', margin: '0 0 4px 0' }}>Sender Profiles</h1>
          <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>Configure SMTP settings for sending emails</p>
        </div>
        <button 
          onClick={() => {
            setEditingProfile(null);
            setFormData({
              name: '',
              from_email: '',
              from_name: '',
              smtp_host: 'smtp.gmail.com',
              smtp_port: 587,
              smtp_username: '',
              smtp_password: '',
              smtp_encryption: 'tls',
              daily_limit: 1000,
              imap_host: 'imap.gmail.com',
              imap_port: 993,
              imap_username: '',
              imap_password: '',
              imap_encryption: 'ssl',
              imap_enabled: false,
            });
            setShowModal(true);
          }}
          style={{ 
            padding: '12px 20px', 
            background: '#3b82f6', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px', 
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span>+</span> Add Profile
        </button>
      </div>

      {/* Profiles Grid */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '20px' }}>
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : profiles.length === 0 ? (
        <div style={{ 
          background: 'white', 
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          padding: '48px 24px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📧</div>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
            No sender profiles yet
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '0.875rem' }}>
            Add your first SMTP configuration to start sending emails
          </p>
          <button 
            onClick={() => setShowModal(true)}
            style={{ 
              padding: '10px 20px', 
              background: '#3b82f6', 
              color: 'white', 
              border: 'none', 
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Add Profile
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '20px' }}>
          {profiles.map((profile) => (
            <div key={profile.id} style={{ 
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
            }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>{profile.name}</h3>
                <span style={{ 
                  padding: '6px 12px', 
                  borderRadius: '9999px',
                  background: profile.is_active ? '#d1fae5' : '#fee2e2',
                  color: profile.is_active ? '#065f46' : '#991b1b',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {profile.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div style={{ marginBottom: '16px' }}>
                <p style={{ margin: '0 0 6px 0', fontSize: '0.875rem', color: '#6b7280' }}>
                  <strong style={{ color: '#374151' }}>From:</strong> {profile.from_name} &lt;{profile.from_email}&gt;
                </p>
                <p style={{ margin: '0 0 6px 0', fontSize: '0.875rem', color: '#6b7280' }}>
                  <strong style={{ color: '#374151' }}>SMTP:</strong> {profile.smtp_host}:{profile.smtp_port}
                </p>
                <p style={{ margin: '0 0 6px 0', fontSize: '0.875rem', color: '#6b7280' }}>
                  <strong style={{ color: '#374151' }}>Encryption:</strong> {profile.smtp_encryption.toUpperCase()}
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                  <strong style={{ color: '#374151' }}>Daily Limit:</strong> {profile.daily_limit} emails
                </p>
              </div>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleEdit(profile)}
                  style={{
                    padding: '10px 16px',
                    background: '#eff6ff',
                    color: '#2563eb',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleTest(profile.id)}
                  disabled={testingProfile === profile.id}
                  style={{ 
                    padding: '10px 16px', 
                    background: testingProfile === profile.id ? '#e5e7eb' : '#eff6ff', 
                    color: testingProfile === profile.id ? '#6b7280' : '#3b82f6', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: testingProfile === profile.id ? 'not-allowed' : 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}
                >
                  {testingProfile === profile.id ? 'Testing...' : 'Test Connection'}
                </button>
                <button 
                  onClick={() => handleDelete(profile.id)}
                  style={{ 
                    padding: '10px 16px', 
                    background: '#fef2f2', 
                    color: '#dc2626', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: 500
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{ 
            background: '#ffffff', 
            padding: '32px', 
            borderRadius: '12px', 
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1f2937', margin: '0 0 24px 0' }}>
              {editingProfile ? 'Edit Sender Profile' : 'Add Sender Profile'}
            </h2>
            <form onSubmit={handleSave}>
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="profileName" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                  Profile Name *
                </label>
                <input
                  id="profileName"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  style={{ 
                    width: '100%', 
                    padding: '12px 16px', 
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                  placeholder="My SMTP Server"
                />
              </div>

              <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b7280', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Email Settings
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="fromName" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    From Name *
                  </label>
                  <input
                    id="fromName"
                    type="text"
                    required
                    value={formData.from_name}
                    onChange={(e) => setFormData({...formData, from_name: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="John Doe"
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="fromEmail" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    From Email *
                  </label>
                  <input
                    id="fromEmail"
                    type="email"
                    required
                    value={formData.from_email}
                    onChange={(e) => setFormData({...formData, from_email: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b7280', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  SMTP Configuration
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="smtpHost" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    SMTP Host *
                  </label>
                  <input
                    id="smtpHost"
                    type="text"
                    required
                    value={formData.smtp_host}
                    onChange={(e) => setFormData({...formData, smtp_host: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="smtpPort" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    Port *
                  </label>
                  <input
                    id="smtpPort"
                    type="number"
                    required
                    value={formData.smtp_port}
                    onChange={(e) => setFormData({...formData, smtp_port: parseInt(e.target.value)})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="smtpUsername" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    Username *
                  </label>
                  <input
                    id="smtpUsername"
                    type="text"
                    required
                    value={formData.smtp_username}
                    onChange={(e) => setFormData({...formData, smtp_username: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="smtpPassword" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    Password *
                  </label>
                  <input
                    id="smtpPassword"
                    type="password"
                    required={!editingProfile}
                    value={formData.smtp_password}
                    onChange={(e) => setFormData({...formData, smtp_password: e.target.value})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="encryption" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    Encryption *
                  </label>
                  <select
                    id="encryption"
                    value={formData.smtp_encryption}
                    onChange={(e) => setFormData({...formData, smtp_encryption: e.target.value as 'tls' | 'ssl' | 'none'})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      background: 'white',
                      boxSizing: 'border-box'
                    }}
                    >
                      <option value="tls">TLS</option>
                      <option value="ssl">SSL</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="dailyLimit" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    Daily Limit *
                  </label>
                  <input
                    id="dailyLimit"
                    type="number"
                    required
                    min="1"
                    value={formData.daily_limit}
                    onChange={(e) => setFormData({...formData, daily_limit: parseInt(e.target.value)})}
                    style={{ 
                      width: '100%', 
                      padding: '10px 14px', 
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.875rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b7280', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  IMAP Configuration (Optional)
                </h3>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={formData.imap_enabled}
                      onChange={(e) => setFormData({...formData, imap_enabled: e.target.checked})}
                      style={{ cursor: 'pointer' }}
                    />
                    Enable IMAP for Inbox Access
                  </label>
                </div>
                {formData.imap_enabled && (
                  <>
                      <div style={{ marginBottom: '16px' }}>
                        <label htmlFor="imapHost" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                          IMAP Host
                        </label>
                        <input
                          id="imapHost"
                          type="text"
                          required={formData.imap_enabled}
                          value={formData.imap_host}
                          onChange={(e) => setFormData({...formData, imap_host: e.target.value})}
                          placeholder="imap.gmail.com"
                          style={{ 
                            width: '100%', 
                            padding: '10px 14px', 
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <label htmlFor="imapPort" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                          IMAP Port
                        </label>
                        <input
                          id="imapPort"
                          type="number"
                          required={formData.imap_enabled}
                          value={formData.imap_port}
                          onChange={(e) => setFormData({...formData, imap_port: parseInt(e.target.value)})}
                          placeholder="993"
                          style={{ 
                            width: '100%', 
                            padding: '10px 14px', 
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <label htmlFor="imapUsername" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                          IMAP Username
                        </label>
                        <input
                          id="imapUsername"
                          type="text"
                          required={formData.imap_enabled}
                          value={formData.imap_username}
                          onChange={(e) => setFormData({...formData, imap_username: e.target.value})}
                          placeholder="your@email.com"
                          style={{ 
                            width: '100%', 
                            padding: '10px 14px', 
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <label htmlFor="imapPassword" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                          IMAP Password
                        </label>
                        <input
                          id="imapPassword"
                          type="password"
                          required={formData.imap_enabled && !editingProfile}
                          value={formData.imap_password}
                          onChange={(e) => setFormData({...formData, imap_password: e.target.value})}
                          placeholder="••••••••"
                          style={{ 
                            width: '100%', 
                            padding: '10px 14px', 
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            outline: 'none',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <label htmlFor="imapEncryption" style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                          IMAP Encryption
                        </label>
                        <select
                          id="imapEncryption"
                          value={formData.imap_encryption}
                          onChange={(e) => setFormData({...formData, imap_encryption: e.target.value as 'ssl' | 'tls' | 'none'})}
                          style={{ 
                            width: '100%', 
                            padding: '10px 14px', 
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '0.875rem',
                            outline: 'none',
                            background: 'white',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="ssl">SSL</option>
                          <option value="tls">TLS</option>
                          <option value="none">None</option>
                        </select>
                      </div>
                    </>
                  )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                <button 
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProfile(null);
                    setFormData({
                      name: '',
                      from_email: '',
                      from_name: '',
                      smtp_host: 'smtp.gmail.com',
                      smtp_port: 587,
                      smtp_username: '',
                      smtp_password: '',
                      smtp_encryption: 'tls',
                      daily_limit: 1000,
                      imap_host: 'imap.gmail.com',
                      imap_port: 993,
                      imap_username: '',
                      imap_password: '',
                      imap_encryption: 'ssl',
                      imap_enabled: false,
                    });
                  }}
                  disabled={submitting}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: '#f3f4f6', 
                    color: '#374151',
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={submitting}
                  style={{ 
                    flex: 1, 
                    padding: '12px', 
                    background: submitting ? '#9ca3af' : '#3b82f6', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {submitting ? (
                    <>
                      <span style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid rgba(255,255,255,0.3)', 
                        borderTopColor: 'white', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite',
                        display: 'inline-block'
                      }} />
                      Saving...
                    </>
                  ) : (
                    editingProfile ? 'Update Profile' : 'Save Profile'
                  )}
                </button>
              </div>
            </form>

            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
};

export default SenderProfiles;
